import { NavigateFunction, useNavigate } from 'react-router-dom';

import { ADMIN_ROUTE_PATH } from '@/administration/constants/adminRoutePaths';
import { QueryParams, TabQueryParam } from '@/features/journey/admin/organisms/JourneyInsightsTabs/JourneyInsightsTabs';
import { slugify } from '@/utils/misc';
import { ROUTE_PATH } from '@/constants/routePaths';

/**
 * A journey's own routes are keyed on the slug already sitting in the URL, not on the title it
 * was made from: `slugify` strips the hyphens it puts in, so running it over a slug a second
 * time produces a different path. A caller holding the title converts it once, on the way in.
 */
type JourneyRouteOps = {
  journeyId: number;
  /** The `:name` path segment, i.e. `slugify(title)`. */
  journeyName: string;
};

const journeyViewHref = ({ journeyId, journeyName }: JourneyRouteOps): string =>
  `${ROUTE_PATH.JOURNEY_BASE}/${journeyId}/${journeyName}`;

export const useJourneyHref = (): {
  adminListHref: string;
  adminEditorHref: (journeyId?: number) => string;
  adminEditCertificateHref: (journeyId?: number) => string;
  adminInsightsHref: (journeyId?: number) => string;
  adminPublishHref: (journeyId?: number) => string;
  adminLearnersEditorHref: (journeyId?: number) => string;
  adminImportLearnersHref: (journeyId?: number) => string;
  adminEditLearningsHref: (journeyId?: number) => string;
  adminEditLearningsLearningAttendanceHref: (journeyId?: number, learningId?: number) => string;
  adminCatalogHref: string;
  adminUsersHref: string;
  managerRootHref: string;
  managerListHref: string;
  managerInsightsHref: (journeyId?: number) => string;
  learnerViewHref: (ops: { journeyId: number; journeyTitle: string }) => string;
  discussionsHref: (ops: JourneyRouteOps) => string;
  discussionsTeamsHref: (ops: JourneyRouteOps) => string;
  discussionsTeamsImportHref: (ops: JourneyRouteOps) => string;
} => {
  return {
    adminListHref: `${ADMIN_ROUTE_PATH.JOURNEYS}/`,
    adminEditorHref: (journeyId?: number) => `${ADMIN_ROUTE_PATH.JOURNEYS}/${journeyId}/edit`,
    adminEditCertificateHref: (journeyId?: number) => `${ADMIN_ROUTE_PATH.JOURNEYS}/${journeyId}/edit-certificate`,
    adminInsightsHref: (journeyId?: number) => `${ADMIN_ROUTE_PATH.JOURNEYS}/${journeyId}/insights`,
    adminPublishHref: (journeyId?: number) => `${ADMIN_ROUTE_PATH.JOURNEYS}/${journeyId}/publish`,
    adminLearnersEditorHref: (journeyId?: number) =>
      `${ADMIN_ROUTE_PATH.JOURNEYS}/${journeyId}/insights?${QueryParams.Tab}=${TabQueryParam.Participants}&${QueryParams.TabScroll}=true`,
    adminImportLearnersHref: (journeyId?: number) => `${ADMIN_ROUTE_PATH.JOURNEYS}/${journeyId}/import-learners`,
    adminEditLearningsHref: (journeyId?: number) => `${ADMIN_ROUTE_PATH.JOURNEYS}/${journeyId}/edit-learnings`,
    adminEditLearningsLearningAttendanceHref: (journeyId?: number, learningId?: number) =>
      `${ADMIN_ROUTE_PATH.JOURNEYS}/${journeyId}/edit-learnings/learning/${learningId}/attendance`,
    adminCatalogHref: `${ADMIN_ROUTE_PATH.CATALOG}`,
    adminUsersHref: `${ADMIN_ROUTE_PATH.USERS}`,
    managerRootHref: `/management/analyze`,
    managerListHref: `/management/analyze/journeys`,
    managerInsightsHref: (journeyId?: number) => `/management/analyze/journeys/${journeyId}`,
    learnerViewHref: (ops: { journeyId: number; journeyTitle: string }) =>
      journeyViewHref({ journeyId: ops.journeyId, journeyName: slugify(ops.journeyTitle) }),
    discussionsHref: (ops: JourneyRouteOps) => `${journeyViewHref(ops)}/discussions`,
    discussionsTeamsHref: (ops: JourneyRouteOps) => `${journeyViewHref(ops)}/discussionsTeamsSetup`,
    discussionsTeamsImportHref: (ops: JourneyRouteOps) => `${journeyViewHref(ops)}/discussionsTeamsImport`,
  };
};

export const useJourneyNavigate = (): {
  navigate: NavigateFunction;
  navigateToList: () => void;
  navigatoToEditor: (journeyId?: number) => void;
  navigatoToEditCertificate: (journeyId?: number) => void;
  navigatoToInsights: (journeyId?: number) => void;
  navigatoToPublish: (journeyId?: number) => void;
  navigatoToLearnersEditor: (journeyId?: number) => void;
  navigateToImportLearners: (journeyId?: number) => void;
  navigatoToEditLearnings: (journeyId?: number) => void;
  navigatoToEditLearningsLearningAttendance: (journeyId?: number, learningId?: number) => void;
  navigateToLearnerView: (ops: { journeyId: number; journeyTitle: string }) => void;
  navigate404: () => void;
  navigateToAdminCatalog: () => void;
  navigateToAdminUsers: () => void;
  navigateToManagerList: () => void;
  navigateToManagerInsights: (journeyId?: number) => void;
} => {
  const navigate = useNavigate();
  const href = useJourneyHref();
  return {
    navigate,
    navigate404: () => navigate(ROUTE_PATH.NOT_FOUND),
    navigateToList: () => navigate(href.adminListHref),

    navigatoToEditor: (journeyId?: number) => {
      if (journeyId) navigate(href.adminEditorHref(journeyId));
    },

    navigatoToEditCertificate: (journeyId?: number) => {
      if (journeyId) navigate(href.adminEditCertificateHref(journeyId));
    },

    navigatoToPublish: (journeyId?: number) => {
      if (journeyId) navigate(href.adminPublishHref(journeyId));
    },

    navigatoToLearnersEditor: (journeyId?: number) => {
      if (journeyId) navigate(href.adminLearnersEditorHref(journeyId));
    },

    navigateToImportLearners: (journeyId?: number) => {
      if (journeyId) navigate(href.adminImportLearnersHref(journeyId));
    },

    navigatoToEditLearnings: (journeyId?: number) => {
      if (journeyId) navigate(href.adminEditLearningsHref(journeyId));
    },

    navigatoToEditLearningsLearningAttendance: (journeyId?: number, learningId?: number) => {
      if (journeyId && learningId) navigate(href.adminEditLearningsLearningAttendanceHref(journeyId, learningId));
    },

    navigatoToInsights: (journeyId?: number) => {
      if (journeyId) navigate(href.adminInsightsHref(journeyId));
    },

    navigateToLearnerView: (ops: { journeyId: number; journeyTitle: string }) => {
      if (ops.journeyId) navigate(href.learnerViewHref(ops));
    },

    navigateToAdminCatalog: () => navigate(href.adminCatalogHref),

    navigateToAdminUsers: () => navigate(href.adminUsersHref),

    navigateToManagerList: () => navigate(href.managerListHref),

    navigateToManagerInsights: (journeyId?: number) => {
      if (journeyId) navigate(href.managerInsightsHref(journeyId));
    },
  };
};
